import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Zap, RefreshCw, ShoppingCart } from 'lucide-react';

const LAYERS = [
  {
    id: 'check', label: 'PI Check', tagColor: '#8B5CF6', tag: 'Layer 1',
    card: () => (
      <div className="space-y-3.5">
        <div className="flex items-center gap-3 pb-3 border-b border-white/[0.06]">
          <span className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]" />
          <span className="text-white font-space font-bold text-sm">High conflict risk</span>
          <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">PI: 23</span>
        </div>
        <p className="text-white/50 text-sm">Receiver will feel <span className="text-white/90 font-medium">attacked and defensive</span>.</p>
        <div className="flex gap-1.5 flex-wrap">
          <span className="px-2.5 py-1 text-[10px] font-semibold rounded-full bg-red-500/15 text-red-400 border border-red-500/20">Aggressive</span>
          <span className="px-2.5 py-1 text-[10px] font-semibold rounded-full bg-orange-500/15 text-orange-400 border border-orange-500/20">High risk</span>
        </div>
        <div className="pt-3 border-t border-white/[0.06] space-y-2">
          <span className="text-[10px] uppercase tracking-widest text-[#8B5CF6] font-bold">PI Rewrite</span>
          <p className="text-white/70 text-sm leading-relaxed italic">&ldquo;I want to ensure we&apos;re aligned on the timeline. Could we review the milestones together?&rdquo;</p>
        </div>
        <div className="shimmer-btn rounded-xl py-2.5 text-center text-white font-space font-bold text-sm flex items-center justify-center gap-2">
          <Check className="w-4 h-4" /> Apply rewrite
        </div>
      </div>
    ),
  },
  {
    id: 'craft', label: 'PI Craft', tagColor: '#06B6D4', tag: 'Layer 2',
    card: () => (
      <div className="space-y-3.5">
        <div className="flex items-center gap-2.5 p-2.5 bg-cyan-500/[0.08] border border-cyan-500/20 rounded-lg">
          <Check className="w-4 h-4 text-cyan-400 flex-shrink-0" />
          <div>
            <p className="text-cyan-400 text-xs font-bold">ChatGPT detected</p>
            <p className="text-white/35 text-[10px]">Applying: role-first structure</p>
          </div>
        </div>
        <div className="bg-white/[0.04] border border-white/[0.06] rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-white/30 text-[9px] font-bold uppercase tracking-wider">Token Meter</span>
            <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 rounded-full px-2 py-0.5">Save 62%</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-white/25 text-[10px] w-10">Before</span>
            <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden"><div className="h-full rounded-full bg-red-500/50 w-full" /></div>
            <span className="text-white/40 text-[10px]">4</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-white/25 text-[10px] w-10">After</span>
            <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-400 w-[38%]" /></div>
            <span className="text-emerald-400 text-[10px]">280</span>
          </div>
        </div>
        <div className="p-2.5 bg-amber-500/[0.04] border border-amber-500/15 rounded-lg">
          <span className="text-[9px] font-bold text-amber-400 uppercase tracking-wider block mb-1.5">Precision Cast</span>
          <p className="text-white/50 text-[11px] font-mono leading-relaxed line-clamp-3">Act as a senior startup strategy consultant specializing in pre-seed B2B SaaS...</p>
        </div>
      </div>
    ),
  },
  {
    id: 'optimizer', label: 'Token Optimizer', tagColor: '#10B981', tag: 'Layer 3',
    card: () => (
      <div className="space-y-3.5">
        <div className="text-center py-2">
          <p className="text-5xl font-space font-bold text-emerald-400">62%</p>
          <p className="text-white/30 text-[10px] uppercase tracking-wider mt-1">Tokens Saved</p>
        </div>
        <div className="bg-white/[0.04] border border-white/[0.06] rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-white/25 text-[10px] w-12">Before</span>
            <div className="flex-1 h-2 bg-white/[0.06] rounded-full overflow-hidden"><div className="h-full rounded-full bg-red-500/40 w-full" /></div>
            <span className="text-white/40 text-[10px] w-16 text-right">340 tokens</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-white/25 text-[10px] w-12">After</span>
            <div className="flex-1 h-2 bg-white/[0.06] rounded-full overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-400 w-[38%]" /></div>
            <span className="text-emerald-400 text-[10px] w-16 text-right">129 tokens</span>
          </div>
        </div>
        <div className="flex items-center justify-between p-2.5 bg-emerald-500/[0.04] border border-emerald-500/15 rounded-lg">
          <div className="flex items-center gap-2 text-xs">
            <span className="line-through text-red-400/50">$0.0102</span>
            <span className="text-white/20">&rarr;</span>
            <span className="text-emerald-400 font-bold">$0.0039</span>
          </div>
          <Zap className="w-4 h-4 text-emerald-400" />
        </div>
        <p className="text-white/30 text-[10px] text-center">Same quality. Fewer tokens. Lower cost.</p>
      </div>
    ),
  },
  {
    id: 'cast', label: 'PI Cast', tagColor: '#F59E0B', tag: 'Layer 4',
    card: () => (
      <div className="space-y-3.5">
        <div className="p-2.5 bg-amber-500/[0.06] border border-amber-500/15 rounded-lg">
          <span className="text-[9px] font-bold text-amber-400 uppercase tracking-wider block mb-1">Your Outcome</span>
          <p className="text-white/60 text-xs italic">&ldquo;Generate a competitive analysis of top 5 CRM platforms&rdquo;</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {['Research', 'ChatGPT', '164 tokens'].map(t => (
            <span key={t} className="px-2 py-0.5 text-[9px] font-semibold rounded bg-white/[0.04] text-white/35 border border-white/[0.06]">{t}</span>
          ))}
        </div>
        <div className="p-2.5 bg-white/[0.03] border border-white/[0.06] rounded-lg">
          <p className="text-white/60 text-[11px] font-mono leading-relaxed line-clamp-4">Act as a senior market research analyst specializing in CRM platforms and B2B SaaS...<br />1. Executive comparison matrix<br />2. Feature-by-feature breakdown<br />3. Pricing tier analysis...</p>
        </div>
        <div className="text-center text-[10px] text-emerald-400 font-bold">One line in &rarr; Expert cast out</div>
      </div>
    ),
  },
  {
    id: 'loop', label: 'PI Loop', tagColor: '#8B5CF6', tag: 'Layer 5',
    card: () => (
      <div className="space-y-3.5">
        <div className="space-y-2">
          {[
            { iter: 1, score: 65, feedback: 'Missing specificity in CTA' },
            { iter: 2, score: 78, feedback: 'Better but lacks social proof' },
            { iter: 3, score: 92, feedback: 'Threshold met' },
          ].map(it => (
            <div key={it.iter} className="flex items-center gap-2 bg-white/[0.03] rounded-lg p-2">
              <span className="text-[10px] font-bold text-white/30 w-5">#{it.iter}</span>
              <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${it.score >= 80 ? 'bg-emerald-500' : it.score >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${it.score}%` }} />
              </div>
              <span className={`text-[10px] font-bold ${it.score >= 80 ? 'text-emerald-400' : 'text-amber-400'}`}>{it.score}%</span>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-2 p-2 bg-emerald-500/[0.06] border border-emerald-500/20 rounded-lg">
          <RefreshCw className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-emerald-400 text-xs font-bold">Threshold met &mdash; 92%</span>
        </div>
        <p className="text-white/30 text-[10px] text-center">Auto-refine until your outcome threshold is met</p>
      </div>
    ),
  },
  {
    id: 'store', label: 'PI Store', tagColor: '#06B6D4', tag: 'Layer 6',
    card: () => (
      <div className="space-y-3.5">
        <div className="grid grid-cols-3 gap-2 text-center">
          {[{ v: '200K+', l: 'Casts' }, { v: '14', l: 'Categories' }, { v: '6', l: 'Platforms' }].map(s => (
            <div key={s.l} className="p-2 bg-white/[0.04] rounded-lg">
              <p className="text-white font-bold text-sm font-space">{s.v}</p>
              <p className="text-white/25 text-[9px] uppercase">{s.l}</p>
            </div>
          ))}
        </div>
        {[
          { title: 'Claude System Prompts', cat: 'Coding', price: 'FREE' },
          { title: 'Sales Email Mastery', cat: 'Sales', price: 'FREE' },
          { title: 'Legal Contract Review', cat: 'Legal', price: 'FREE' },
        ].map(c => (
          <div key={c.title} className="flex items-center gap-2 p-2 bg-white/[0.03] border border-white/[0.06] rounded-lg">
            <div className="flex-1 min-w-0">
              <p className="text-white/70 text-[11px] font-medium truncate">{c.title}</p>
              <p className="text-white/25 text-[9px]">{c.cat}</p>
            </div>
            <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 rounded-full px-2 py-0.5">{c.price}</span>
          </div>
        ))}
        <div className="flex items-center justify-center gap-2 text-[10px] text-[#06B6D4]">
          <ShoppingCart className="w-3 h-3" /> <span className="font-bold">Create, sell, discover casts</span>
        </div>
      </div>
    ),
  },
];

export function HeroLayerShowcase() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive(prev => (prev + 1) % LAYERS.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const layer = LAYERS[active];

  return (
    <div className="relative" data-testid="hero-showcase">
      {/* Glow behind card */}
      <div className="absolute -inset-8 rounded-3xl opacity-[0.08]"
        style={{ background: `radial-gradient(circle at center, ${layer.tagColor} 0%, transparent 70%)` }} />
      <div className="absolute -inset-6 rounded-3xl bg-white/[0.02] border border-white/[0.04] -z-10" />

      {/* Layer tabs */}
      <div className="flex gap-1 mb-3 overflow-x-auto pb-1 scrollbar-hide">
        {LAYERS.map((l, i) => (
          <button key={l.id} onClick={() => setActive(i)}
            className={`px-2.5 py-1 text-[9px] font-bold rounded-full whitespace-nowrap transition-all ${active === i ? 'text-white' : 'text-white/25 hover:text-white/50'}`}
            style={active === i ? { backgroundColor: `${l.tagColor}20`, color: l.tagColor, border: `1px solid ${l.tagColor}30`, boxShadow: `0 0 12px ${l.tagColor}15` } : { border: '1px solid transparent' }}>
            {l.label}
          </button>
        ))}
      </div>

      {/* Card */}
      <div className="glass rounded-2xl p-5 w-[340px] min-h-[320px]">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color: layer.tagColor }}>{layer.tag} &middot; {layer.label}</span>
          <div className="flex gap-1">
            {LAYERS.map((_, i) => (
              <span key={`dot-${i}`} className={`w-1.5 h-1.5 rounded-full transition-all ${i === active ? 'scale-125' : ''}`}
                style={{ backgroundColor: i === active ? layer.tagColor : 'rgba(255,255,255,0.1)' }} />
            ))}
          </div>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={layer.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
          >
            <layer.card />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Auto-play indicator */}
      <div className="mt-3 flex justify-center">
        <div className="h-[2px] w-24 bg-white/[0.04] rounded-full overflow-hidden">
          <motion.div
            key={active}
            className="h-full rounded-full"
            style={{ background: `linear-gradient(90deg, ${layer.tagColor}, ${layer.tagColor}60)` }}
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: 4, ease: 'linear' }}
          />
        </div>
      </div>
    </div>
  );
}
